import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

import {
  getPlansSummary,
  getPlansSummaryQueryKey,
  type PlanReport,
  type SystemPlan,
} from '@cfa/pathway-fe-shared/xp';

import useLeaderOrLicenseeLocations from '@/hooks/useLeaderOrLicenseeLocations';
import { type AddSortFields, addSortFields } from '@/hooks/useSortOptions';
import useSystemPlans from '@/hooks/useSystemPlans';

import { getBroadQueryKeyId } from '@/utils/getBroadQueryKeyId';

import { queryClient } from '@/services';

import { reportsPlansAtom } from '../state';

const isReadonlySubscribed = (planId: string, systemPlans: SystemPlan[]) =>
  systemPlans.some(
    systemPlan => systemPlan.readonly && systemPlan.id === planId,
  );

export type ReportsPlanRow = AddSortFields<PlanReport> & {
  isReadonlySubscribed: boolean;
};

export const useGetPlansSummary = () => {
  const { licenseeModeToggle: isLicenseeMode } = useAtomValue(reportsPlansAtom);
  const userLocations = useLeaderOrLicenseeLocations({ isLicenseeMode });
  const { systemPlans, isFetchingSystemPlans } = useSystemPlans();

  const enabled = userLocations.length > 0 && !isFetchingSystemPlans;

  return useQuery({
    queryKey: getPlansSummaryQueryKey({
      query: { locationNumbers: userLocations },
    }),
    queryFn: () =>
      getPlansSummary({ query: { locationNumbers: userLocations } }).then(
        response => response.data ?? [],
      ),
    enabled,
    select: (plans): ReportsPlanRow[] =>
      plans
        .map(report => ({
          ...addSortFields(report),
          isReadonlySubscribed: isReadonlySubscribed(report.id, systemPlans),
        }))
        .filter(row => row.isReadonlySubscribed || row.type === 'STORE'),
  });
};

export const invalidateReportsPlans = () =>
  queryClient.invalidateQueries({
    queryKey: [getBroadQueryKeyId(getPlansSummaryQueryKey)],
    refetchType: 'all',
  });
